import moment from "moment";
import {isValidEmail, onlyAlphaNumeric} from "../formatString";

export enum ParseError {
  INVALID_FORMAT = "INVALID_FORMAT",
  VALUE_NOT_PROVIDED = "VALUE_NOT_PROVIDED",
  VALUE_TOO_LONG = "VALUE_TOO_LONG",
}

export enum AvailableColumns {
  CPF_CNPJ = "CPF/CNPJ",
  EMAIL = "E-mail",
  VALOR_CASHBACK = "Valor Cashback",
  VALOR_COMPRA = "Valor Compra",
  DATA_EXPIRACAO = "Data de Expiração",
  DATA_APROVACAO = "Data de Aprovação",
  CODIGO_PEDIDO = "Código",
  TELEFONE = "Telefone",
}

/** Colunas obrigatórias */
export const requiredCols = [
  AvailableColumns.CPF_CNPJ,
  AvailableColumns.VALOR_CASHBACK,
  AvailableColumns.DATA_EXPIRACAO,
];

export type Row = {
  order_code?: number;
  email: string;
  customer_document: string;
  cashback_value: number | null | ParseError;
  purchase_value?: number | null | ParseError;
  expiration_date: string;
  approval_date?: string;
  phone_number?: string;
};

/** O formato considerado é APENAS dd/mm/yyyy */
const isValidBrazilianDate = (dateStr: string): boolean => {
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) return false;

  const [day, month, year] = dateStr.split("/").map(Number);

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const dt = new Date(year, month - 1, day);

  // Verifica se a data é parseada corretamente, formatos incorretos gerariam valores discrepantes
  return (
    dt.getFullYear() === year &&
    dt.getMonth() === month - 1 &&
    dt.getDate() === day
  );
};

/** Verifica se uma string representa um cpf/cnpj valido (apenas pontuação) */
const isValidCustomerDocument = (value: any): boolean => {
  if (typeof value !== "string") return false;

  const cpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
  const cnpjRegex = /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}$/;

  return cpfRegex.test(value) || cnpjRegex.test(value);
};

/**
 * Confere se o input é válido de acordo com os critérios:
 *  - 2 casas decimais no máximo
 *  - sem separador de milhar, ex 10.000,00
 *  - só permite valores numéricos e a string R$ no início
 */
export const parseBRL = (
  col: AvailableColumns,
  value: unknown
): number | null | ParseError => {
  const empty = value === null || value === undefined || value === "";
  // Se for coluna obrigatória e estiver vazio retorna o erro adequado
  if (empty && requiredCols.includes(col)) {
    return ParseError.VALUE_NOT_PROVIDED;
  }

  // Se não for coluna obrigatória e estiver vazio retorna null
  if (empty && !requiredCols.includes(col)) {
    return null;
  }

  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return ParseError.INVALID_FORMAT;
  }

  // Remove "R$" e espaços em branco e substitui a vírgula por ponto
  const cleaned = value
    .replace(/R\$/i, "")
    .replace(/\s+/g, "")
    .replace(",", ".");

  const num = Number(cleaned);

  if (isNaN(num)) {
    return ParseError.INVALID_FORMAT;
  }

  // Permite no máximo 2 casas decimais
  if (!Number.isInteger(num)) {
    const decimalPlaces = num.toString().split(".")[1].length;
    if (decimalPlaces > 2) {
      return ParseError.INVALID_FORMAT;
    }
  }

  return isNaN(num) ? ParseError.INVALID_FORMAT : num;
};

const isValidPhone = (
  value: unknown,
  min: number = 8,
  max: number = 15
): boolean => {
  if (typeof value !== "string") return false;

  const digits = value.replace(/\D/g, ""); // optional: keep only numbers

  return digits.length >= min && digits.length <= max;
};

export const parsePhoneNumber = (phoneNumber: any) => {
  if (!phoneNumber) {
    // Campo não obrigatório, então e não informado não há necessidade de passar por validações
    return phoneNumber;
  }
  if (typeof phoneNumber !== "string" && typeof phoneNumber !== "number") {
    return ParseError.INVALID_FORMAT;
  }

  const trimmed = String(phoneNumber).trim();

  // Campo não obrigatório e não informado, retorna o valor vazio
  if (trimmed.length === 0) {
    return trimmed;
  }

  if (!isValidPhone(trimmed)) {
    return ParseError.INVALID_FORMAT;
  }
  return trimmed;
};

/**
 *
 * @param email
 * @returns
 */
export const parseEmail = (email: any) => {
  if (typeof email !== "string") {
    return ParseError.INVALID_FORMAT;
  }

  const trimmed = email.trim();

  if (trimmed && !isValidEmail(trimmed)) {
    return ParseError.INVALID_FORMAT;
  }
  return trimmed;
};

/**
 * Valida documento de identidade
 * @param documentNumber
 * @returns
 */
export const parseCustomerDocument = (documentNumber: any) => {
  const trimmed = onlyAlphaNumeric(documentNumber.trim());

  // CPF/CNPJ é sempre obrigatório
  if (!trimmed) {
    return ParseError.VALUE_NOT_PROVIDED;
  }

  if (!isValidCustomerDocument(documentNumber)) {
    return ParseError.INVALID_FORMAT;
  }
  return documentNumber;
};

/**
 * Valida se a string de data segue o padrão dd/mm/yyyy.
 * - Permite entradas com formato d/m/yyyy
 * - O retorno vai ser convertido para o padrão yyyy-mm-dd
 * @param column nome da coluna, usado para validar se o campo é obrigatório
 * @param dateStr
 */
export const parseBrazilianDate = (
  column: AvailableColumns,
  dateStr: string
): string => {
  // Se a coluna é obrigatória e nenhum valor foi informado então há um retorno específico
  if (requiredCols.includes(column) && !dateStr) {
    return ParseError.VALUE_NOT_PROVIDED;
  }
  // Coluna não obrigatória que apenas não foi informada
  if (!dateStr && !requiredCols.includes(column)) {
    return "";
  }

  // Data informada em um formato inválido
  if (!isValidBrazilianDate(dateStr)) {
    return ParseError.INVALID_FORMAT;
  }

  const formatos = ["D/M/YYYY", "DD/MM/YYYY"];

  const date = moment(dateStr, formatos, true);

  if (!date.isValid()) {
    return ParseError.INVALID_FORMAT;
  }

  // retorna no padrão yyyy-mm-dd
  return date.format("YYYY-MM-DD");
};
